import React, { useEffect, useState } from 'react';
import { BgIcon, DocumentCategory } from '../../../assets/svg';
import { getApplicationOverview, getLocalStorageItem } from '../../../utils';
import { popup } from '../../../utils/common';
import { API, HEADERS, isAdminRole, LOCAL_STORAGE, OVERVIEW } from '../../../constants';
import { ConfirmationBox, CustomDropdown } from '../../shared';
import { registrationAndDocumentDropdownOptions } from '../../../constants/selectOptions';
import useWindowSize from '../../../hooks/useWindowSize';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { setLoaderVisibility } from '../../../redux';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../../constants/queryKey';
import CircleSpinner from '../../../assets/svg/CircleSpinner';

export default function OverView() {
    const dispatch = useDispatch();
    const { width } = useWindowSize();
    const [newRegistrationRange, setNewRegistrationRange] = useState(
        registrationAndDocumentDropdownOptions[0],
    );
    const [newDocumentRange, setNewDocumentRange] = useState(
        registrationAndDocumentDropdownOptions[0],
    );
    const role = useSelector((state) => state.user.role);
    const [confirm, setConfirm] = React.useState(false);
    const [openConfirmationBox, setOpenConfirmationBox] = useState(false);

    const { data, error, isError, isFetching } = useQuery({
        queryKey: [QUERY_KEYS.OVERVIEW, newDocumentRange.value, newRegistrationRange.value],
        queryFn: () =>
            getApplicationOverview({
                newDocumentRange: newDocumentRange.value,
                newRegistrationRange: newRegistrationRange.value,
            }),
        select: (data) => data?.data?.[0],
        placeholderData: {
            new_registrations: 0,
            new_docs_created: 0,
        },
    });
    if (isError) popup('error', error?.message);

    const handleDownload = async () => {
        try {
            dispatch(setLoaderVisibility(true));
            const response = await fetch(
                // eslint-disable-next-line no-undef
                `${process.env.REACT_APP_BASE_URL}${API.USER_REPORT_EXCEL}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `${HEADERS.AUTHORIZATION} ${getLocalStorageItem(LOCAL_STORAGE.AUTH_TOKEN)}`,
                    },
                },
            );

            if (!response.ok) throw new Error('Failed to download file');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                response.headers.get('X-File-Name') ||
                    `weekly_users_login_report_${Date.now()}.xlsx`,
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            popup('error', error.msg || error.message || 'Unable to download the file');
        } finally {
            dispatch(setLoaderVisibility(false));
            setConfirm(false);
        }
    };

    useEffect(() => {
        if (confirm) {
            handleDownload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirm]);

    return (
        <div className='dashboard-header ssc-shadow'>
            <span
                className='d-flex align-items-center justify-content-between mb-lg-4 mb-2'
                style={{ height: '40px' }}
            >
                <h2 className='title '>{OVERVIEW.TITLE}</h2>
                {isAdminRole(role) && (
                    <>
                        {width > 590 ? (
                            <button
                                className='ssc-secondary-white-btn '
                                type='button'
                                onClick={() => setOpenConfirmationBox(true)}
                            >
                                <DocumentCategory stroke='currentColor' />
                                Generate Weekly Users Report
                            </button>
                        ) : (
                            <span
                                id='action-btn-green'
                                title={'Generate Weekly Users Report'}
                                onClick={() => setOpenConfirmationBox(true)}
                            >
                                <span className='text-white'>
                                    <DocumentCategory stroke='#fff' />
                                </span>
                            </span>
                        )}
                    </>
                )}
            </span>
            <div className='row d-flex gap-2'>
                <div className='col-md-4 custom-col mb-lg-0 mb-1'>
                    <div className='box-container ssc-shadow h-100 p-lg-4 p-md-3 p-2 bg-white d-flex align-items-start justify-content-start flex-column'>
                        <div className='box-title d-flex align-items-center'>
                            <BgIcon />
                            <p className='mb-0'>{OVERVIEW.NEW_REGISTRATIONS}</p>
                        </div>
                        <div className='box-num-count d-flex align-items-center justify-content-between w-100 mb-2'>
                            {isFetching ? (
                                <CircleSpinner size='2rem' />
                            ) : (
                                <h4 className='mb-0 me-2'>{data?.new_registrations}</h4>
                            )}
                            <CustomDropdown
                                options={registrationAndDocumentDropdownOptions}
                                handleOptionSelect={(option) => {
                                    setNewRegistrationRange(option);
                                }}
                                minWidth='120px'
                                selectedOption={newRegistrationRange.id}
                            />
                        </div>
                    </div>
                </div>
                <div className='col-md-4 custom-col mb-lg-0 mb-1'>
                    <div className='box-container ssc-shadow h-100 p-lg-4 p-md-3 p-2 bg-white d-flex align-items-start justify-content-start flex-column'>
                        <div className='box-title d-flex align-items-center'>
                            <BgIcon />
                            <p className='mb-0'>{OVERVIEW.DOCUMENTS_CREATED}</p>
                        </div>
                        <div className='box-num-count d-flex align-items-center justify-content-between w-100 mb-2'>
                            {isFetching ? (
                                <CircleSpinner size='2rem' />
                            ) : (
                                <h4 className='mb-0 me-2'>{data?.new_docs_created}</h4>
                            )}
                            <CustomDropdown
                                options={registrationAndDocumentDropdownOptions}
                                handleOptionSelect={(option) => {
                                    setNewDocumentRange(option);
                                }}
                                minWidth='120px'
                                selectedOption={newDocumentRange.id}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {
                <ConfirmationBox
                    setConfirm={setConfirm}
                    openConfirmationBox={openConfirmationBox}
                    setOpenConfirmationBox={setOpenConfirmationBox}
                    customMessage={`Are you sure you want to download "WEEKLY USER LOGIN REPORT" in excel format?`}
                    showCloseIcon
                />
            }
        </div>
    );
}
